import React, { Fragment } from "react";
import { useTable } from "react-table";
import { useIntl } from "react-intl";
import { modifyCell, modifyCellHeader } from "./cell_formatter";
import Images from "../../utils/styles/images";
import messages from "./messages";
import Icon from "../../components/Icon";

/**
 * The table every NAV view renders.
 *
 * Paging and sorting are done by the server (there are 128,466 NAV rows), so `data` is already the
 * page the operator asked for. That is why usePagination/useSortBy are not plugged in here: with
 * usePagination the table would silently slice the fetched rows down to its own pageSize, and
 * useSortBy would reorder only the rows on screen - the exact bug FundList has.
 *
 * `tableMinWidth` is what makes the wide NAV table scroll. The container is `overflow-x: auto`, but
 * the table itself is `table-layout: fixed; width: 100%`, so per-column widths only ever divide the
 * available space; without a min-width in pixels fourteen columns squash instead of scrolling.
 */
function Table({
  columns,
  data,
  handleActionHeaderClick,
  sortBy,
  sortOrder,
  errorMessage,
  onRetry,
  tableMinWidth
}) {
  const intl = useIntl();
  const { getTableProps, getTableBodyProps, headerGroups, prepareRow, rows } = useTable({
    data,
    columns
  });

  const renderBodyPlaceholder = () => {
    // A failed request must never look like "no rows matched". It says so, and offers a retry.
    if (errorMessage) {
      return (
        <tr className="nav-table-empty-row">
          <td colSpan={columns.length}>
            <div className="nav-table-no-data">
              <img src={Images.error} alt="request-failed" />
              <p className="nav-table-error-header">
                {intl.formatMessage(messages.errorHeader)}
              </p>
              <p className="nav-table-no-data-body">{errorMessage}</p>
              {onRetry ? (
                <button className="nav-retry-button" onClick={onRetry}>
                  <Icon name="refresh" size={16} />
                  {intl.formatMessage(messages.buttonRetry)}
                </button>
              ) : null}
            </div>
          </td>
        </tr>
      );
    }

    return (
      <tr className="nav-table-empty-row">
        <td colSpan={columns.length}>
          <div className="nav-table-no-data">
            <img src={Images.noData.board} alt="no-data-found-board" />
            <p className="nav-table-no-data-header">
              {intl.formatMessage(messages.emptyHeader)}
            </p>
            <p className="nav-table-no-data-body">
              {intl.formatMessage(messages.emptyBody)}
            </p>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <Fragment>
      <div className="nav-list-container">
        <table
          className="nav-table"
          style={{ minWidth: tableMinWidth }}
          {...getTableProps()}
        >
          <thead className="nav-head-container">
            {headerGroups.map(headerGroup => (
              <tr {...headerGroup.getHeaderGroupProps()} className="nav-head-row">
                {headerGroup.headers.map((column, i) => (
                  <th
                    key={`nav-header-${i}`}
                    className="nav-header"
                    style={{ width: column.render("width") }}
                  >
                    {modifyCellHeader(column, handleActionHeaderClick, sortBy, sortOrder)}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps} className="nav-table-body">
            {rows.length <= 0
              ? renderBodyPlaceholder()
              : rows.map(row => {
                  prepareRow(row);
                  return (
                    <tr {...row.getRowProps()} className="nav-table-row">
                      {row.cells.map(cell => {
                        return (
                          <td
                            {...cell.getCellProps()}
                            className="nav-cell"
                            style={{
                              width: cell.column.width,
                              textAlign: cell.column.numeric ? "right" : "left"
                            }}
                          >
                            {modifyCell(cell, intl)}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
          </tbody>
        </table>
      </div>
    </Fragment>
  );
}

export default Table;
